import {
  createSelector,
  createSlice,
  isAnyOf,
  PayloadAction,
} from '@reduxjs/toolkit';
import type { Dispatch } from 'redux';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {
  SOURCE_ADMIN_SMS,
  SOURCE_TCAPI,
} from '@rsos/constants/locationSources';
import {
  CONVERSATION_END,
  CONVERSATION_START,
  CONVERSATION_UPDATE,
  type BaseConversation,
} from '@rsos/constants/streamWs/conversation';
import { User } from '@rsos/utils/useTalkJS';
import { getErrorStatus } from '../../utils/errorUtils';
import {
  adminSmsApi,
  invalidateAdminSmsConversationMetadata,
} from './adminSmsApi';
import {
  ConversationLoadingRecord,
  conversationStateValues,
} from './conversations.typings';
import { invalidateTCAPIConversationMetadata, tcapiAPI } from './tcapiAPI';
import { getIsConversationEnded } from './utils';

/**
 * A Redux slice that contains and bundles all possible conversations and their data together
 * Only stores 1 main conversation at a time
 */
const conversationsSlice = createSlice({
  name: 'conversations',
  initialState: {} as Record<string, conversationStateValues>,
  reducers: {
    setConversationUnread: (
      state,
      action: PayloadAction<{
        isUnread: boolean;
        messageID?: string;
        callerID: string | null | undefined;
      }>,
    ) => {
      const { isUnread, callerID } = action.payload;

      if (!callerID) return;

      if (state[callerID]) {
        state[callerID] = {
          ...state[callerID],
          isUnread: isUnread,
        };
      }
    },
    addConversation: (
      state,
      action: PayloadAction<BaseConversation['body']>,
    ) => {
      const body = action.payload;
      const callerID = body?.caller_id;

      if (!callerID) return;

      const hasPreviousConversation = !!state[callerID]?.data;

      state[callerID] = {
        data: body,
        source: body.source,
        isUnread: hasPreviousConversation,
        loading: {} as ConversationLoadingRecord,
        recentMessageType: CONVERSATION_START,
        participants: [],
      } as conversationStateValues;
    },
    updateConversation: (
      state,
      action: PayloadAction<{
        body: BaseConversation['body'];
        messageType: typeof CONVERSATION_UPDATE | typeof CONVERSATION_END;
      }>,
    ) => {
      const body = action.payload.body;
      const callerID = body?.caller_id;

      if (!callerID) {
        // eslint-disable-next-line no-console -- logging for sentry
        console.error(
          'updateConversation: WS message body had no caller_id; ignoring.',
        );
        return;
      }

      if (!state[callerID]) {
        // eslint-disable-next-line no-console -- logging for sentry
        console.error(
          `updateConversation: no conversation in local state for callerID ${callerID}; ignoring.`,
        );
        return;
      }

      // For TCAPI, RTK Query (TCAPIMetaData) is the source of truth for
      // session state. WS update/end messages are broadcast on the caller-id
      // topic and may carry a sibling agency's conversation_id (during
      // transfer flows), which would corrupt the local view if spread into
      // `data`. Take recentMessageType from the message — needed by toast and
      // unread logic — and let RTK Query refresh `data` after the dispatch
      // site invalidates the TCAPIMetaData tag.
      if (state[callerID].source === SOURCE_TCAPI) {
        state[callerID].recentMessageType = action.payload.messageType;
        return;
      }

      state[callerID] = {
        ...state[callerID],
        recentMessageType: action.payload.messageType,
        data: {
          ...state[callerID].data,
          ...body,
        },
      } as conversationStateValues;
    },
    addParticipant: (
      state,
      action: PayloadAction<{
        callerID?: string | null;
        participant: User;
      }>,
    ) => {
      const { callerID, participant } = action.payload;

      if (!callerID || !state[callerID]) {
        return;
      }

      const conversation = state[callerID];

      // Ensure participants array exists
      if (!conversation.participants) {
        conversation.participants = [];
      }

      if (conversation.participants.find(p => p.id === participant.id)) {
        return;
      }

      conversation.participants = [...conversation.participants, participant];
    },
  },
  extraReducers: builder => {
    builder
      .addMatcher(
        tcapiAPI.endpoints.getTCAPIConversationMetadata.matchFulfilled,
        (state, action) => {
          const callerID: string = action.meta.arg.originalArgs.callerID;
          const existingConversation = state[callerID];

          // A non-TCAPI conversation (e.g. admin_sms, delivered via websocket)
          // already owns this callerID. With keepUnusedDataFor: Infinity a
          // stale TCAPI session stays cached and could clobber the live entry,
          // so only let the TCAPI session replace it when the TCAPI session is
          // actually newer; otherwise keep the existing conversation.
          if (
            existingConversation &&
            existingConversation.source !== SOURCE_TCAPI
          ) {
            const existingTime =
              existingConversation.data?.conversation_start_time ?? 0;
            const incomingTime =
              action.payload?.creation_time ??
              action.payload?.last_updated_time ??
              0;
            if (incomingTime <= existingTime) {
              return;
            }
          }

          // Replace a stale ended TCAPI session with new active/pending session data
          // This prevents old ended sessions from blocking new sessions on the same number
          if (
            existingConversation?.source === SOURCE_TCAPI &&
            getIsConversationEnded(existingConversation) &&
            !getIsConversationEnded({
              source: SOURCE_TCAPI,
              data: action.payload,
            } as conversationStateValues)
          ) {
            delete state[callerID];
          }

          // Keep ended sessions in state to allow UI to hide buttons and show "Closed Chat" status
          state[callerID] = {
            ...state[callerID],
            source: SOURCE_TCAPI,
            data: action.payload,
            transfer: action.payload?.transfer ?? undefined,
            loading: {
              ...state?.[callerID]?.loading,
              getConversationMetadata: false,
            },
          };
        },
      )
      .addMatcher(
        tcapiAPI.endpoints.getTCAPIConversationMetadata.matchPending,
        (state, action) => {
          const callerID: string = action.meta.arg.originalArgs.callerID;

          // A non-TCAPI conversation (e.g. admin_sms) already owns this
          // callerID — a spurious in-flight TCAPI metadata fetch must not flip
          // its source to tcapi or disturb its data while loading.
          if (state[callerID] && state[callerID].source !== SOURCE_TCAPI) {
            return;
          }

          const previous = state[callerID];

          // Keep whatever is already known in place while the fetch runs. This
          // matcher fires on every websocket-triggered refetch of a live
          // conversation, and blanking the entry unmounts the chat, drops the
          // action buttons and clears the unread badge mid-conversation.
          state[callerID] = {
            ...previous,
            source: SOURCE_TCAPI,
            data: previous?.data ?? null,
            loading: {
              ...previous?.loading,
              getConversationMetadata: true,
            },
            isUnread: previous?.isUnread ?? false,
          };
        },
      )
      .addMatcher(
        tcapiAPI.endpoints.getTCAPIConversationMetadata.matchRejected,
        (state, action) => {
          const callerID: string = action.meta.arg.originalArgs.callerID;

          // matchRejected is `isRejected(thunk) && matchesEndpoint(name)` with
          // no isRejectedWithValue filter, so it also fires for rejections that
          // carry no payload: an aborted thunk (an unmounting subscriber
          // cancels its in-flight fetch), and a condition short-circuit, since
          // queryThunk sets dispatchConditionRejection. Neither means a request
          // ran and failed, so neither says anything about the session.
          if (action.meta.condition || action.meta.aborted) {
            return;
          }

          // Only touch TCAPI-owned entries: a non-TCAPI conversation (e.g.
          // admin_sms) on this callerID must survive a spurious TCAPI 404.
          if (state[callerID] && state[callerID].source !== SOURCE_TCAPI) {
            return;
          }

          // 404 is this endpoint's "no active session" answer, so the entry has
          // to go — a falsy conversationSource is what lets CallChats fall back
          // to the real session type (TF911, RCS) instead of routing a
          // non-TCAPI session to the TCAPI APIs. An entry with no data is the
          // speculative `source: 'tcapi'` stamp matchPending puts on every SMS
          // caller, so it goes for the same reason.
          //
          // Any other rejection — a transport failure that never reached the
          // server, a 5xx — says nothing about whether the session exists.
          // Deleting a session we have already seen on the strength of one
          // failed request costs the operator a live chat that no refetch
          // brings back.
          const conversation = state[callerID];
          const isNoSuchSession = getErrorStatus(action.payload) === 404;

          if (isNoSuchSession || !conversation?.data) {
            delete state[callerID];
            return;
          }

          conversation.loading.getConversationMetadata = false;
        },
      )
      .addMatcher(
        tcapiAPI.endpoints.createTCAPIConversation.matchPending,
        (state, action) => {
          const { callerID } = action.meta.arg.originalArgs;
          if (state[callerID]) {
            state[callerID].loading.createConversation = true;
          }
        },
      )
      .addMatcher(
        isAnyOf(
          tcapiAPI.endpoints.createTCAPIConversation.matchFulfilled,
          tcapiAPI.endpoints.createTCAPIConversation.matchRejected,
        ),
        (state, action) => {
          const { callerID } = action.meta.arg.originalArgs;
          if (state[callerID]) {
            state[callerID].loading.createConversation = false;
          }
        },
      )
      .addMatcher(
        adminSmsApi.endpoints.getAdminSmsConversationMetadata.matchPending,
        (state, action) => {
          const callerID: string = action.meta.arg.originalArgs.callerID;

          // A non-admin_sms conversation already owns this callerID — don't flip
          // its source or clear its data while the admin_sms fetch is in-flight.
          if (state[callerID] && state[callerID].source !== SOURCE_ADMIN_SMS) {
            return;
          }

          if (state[callerID]) {
            state[callerID].loading = {
              ...state[callerID].loading,
              getConversationMetadata: true,
            };
          }
        },
      )
      .addMatcher(
        adminSmsApi.endpoints.getAdminSmsConversationMetadata.matchFulfilled,
        (state, action) => {
          const callerID: string = action.meta.arg.originalArgs.callerID;
          const existingConversation = state[callerID];

          // A non-admin_sms conversation (e.g. tcapi) already owns this callerID.
          // With keepUnusedDataFor: Infinity a stale admin_sms session stays cached
          // and could clobber the live entry, so only let the admin_sms session
          // replace it when the admin_sms session is actually newer; otherwise keep
          // the existing conversation.
          if (
            existingConversation &&
            existingConversation.source !== SOURCE_ADMIN_SMS
          ) {
            const existingTime =
              existingConversation.data?.last_updated_time ??
              existingConversation.data?.creation_time ??
              0;
            const incomingTime = action.payload?.last_updated_time ?? 0;
            if (incomingTime <= existingTime) {
              return;
            }
          }

          // Replace stale inactive admin_sms session with new active session data
          if (
            existingConversation?.source === SOURCE_ADMIN_SMS &&
            getIsConversationEnded(existingConversation) &&
            !['ended', 'inactive'].includes(action.payload.status)
          ) {
            delete state[callerID];
          }

          state[callerID] = {
            ...state[callerID],
            source: SOURCE_ADMIN_SMS,
            data: {
              ...state?.[callerID]?.data,
              ...action.payload,
            },
            loading: {
              ...state?.[callerID]?.loading,
              getConversationMetadata: false,
            },
          } as conversationStateValues;
        },
      );
  },
});

export function selectConversation(callerId: string) {
  return (state: {
    conversations: Record<string, conversationStateValues>;
  }) => {
    return state.conversations?.[callerId];
  };
}

export function selectConversationId(callerId: string) {
  return createSelector(
    [selectConversation(callerId)],
    (conversation?: conversationStateValues) => {
      if (!conversation) {
        return undefined;
      }
      switch (conversation.source) {
        case SOURCE_ADMIN_SMS:
        case SOURCE_TCAPI:
          return conversation.data?.conversation_id;
      }
    },
  );
}

export function selectConversationUnread(callerId: string) {
  return createSelector(
    [selectConversation(callerId)],
    conversation => conversation?.isUnread,
  );
}

const persistConfig = {
  key: 'conversation',
  storage,
};

export const conversationsReducer = conversationsSlice.reducer;

export const {
  setConversationUnread,
  addConversation,
  updateConversation,
  addParticipant,
} = conversationsSlice.actions;

/**
 * Apply a conversation lifecycle update from a websocket message.
 *
 * Encapsulates the invariant that handling a TCAPI lifecycle event also
 * invalidates the TCAPIMetaData RTK Query cache for that callerID. The slice
 * deliberately does not spread the WS body into `data` for TCAPI sources
 * (the body is broadcast on the caller-id topic and may carry a sibling
 * agency's conversation_id during transfer flows); the cache invalidation
 * here is what triggers a re-fetch of the authoritative session for our
 * orgName, and matchFulfilled hydrates `data` with the correct payload.
 */
export function applyConversationUpdate(payload: {
  body: BaseConversation['body'];
  messageType: typeof CONVERSATION_UPDATE | typeof CONVERSATION_END;
}) {
  return (dispatch: Dispatch) => {
    dispatch(updateConversation(payload));
    if (payload.body.source === SOURCE_TCAPI && payload.body.caller_id) {
      dispatch(invalidateTCAPIConversationMetadata(payload.body.caller_id));
    }
    if (payload.body.source === SOURCE_ADMIN_SMS && payload.body.caller_id) {
      dispatch(invalidateAdminSmsConversationMetadata(payload.body.caller_id));
    }
  };
}

/**
 * Receive a TCAPI soft transfer addressed to our agency from a websocket
 * message.
 *
 * The WS message is treated as a "session changed, refetch" trigger only —
 * transfer state itself comes from the TCAPI session GET response, which
 * matchFulfilled hydrates into both ``data`` and ``transfer``. With
 * ``keepUnusedDataFor: Infinity`` a prior session for the same caller can
 * leave a populated cache in place, so the next subscription returns cached
 * data without dispatching matchFulfilled. Invalidating the TCAPIMetaData
 * tag forces the next subscription to hit the network so the receiver
 * picks up the authoritative session.
 */
export function applyReceivedTransfer(callerID: string) {
  return (dispatch: Dispatch) => {
    dispatch(invalidateTCAPIConversationMetadata(callerID));
  };
}

const persistedReducer = persistReducer(
  persistConfig,
  conversationsSlice.reducer,
);

export default persistedReducer;
